/*
 * Copyright (c) 2022 E-WIND - Thais Watanabe.
 * All rights reserved.
 */
import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";

import { queryInspectionModels } from "src/services/AmplifyQuery";

import Layout from "src/components/layouts/Layout.react";
import LoadingCard from "src/components/loading/LoadingCard.react";
import InspectionInfo from "src/components/cards/InspectionInfo.react";

import { PrinterIcon } from "@heroicons/react/24/outline";

import Result from "src/views/report/Result.react";

/**
 * The Report component
 *
 * @author Thais Watanabe
 */
const Report = () => {
  const DEFAULT_ACTIVE_TAB = 1;

  const { activeTab } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { inspection, turbineInspection, lastTurbineInspections } =
    location.state;

  const [inspectionModels, setInspectionModels] = useState(null);

  const PrintButton = (props) => {
    const {
      inspection,
      turbineInspection,
      lastTurbineInspections,
      inspectionModels,
    } = props;
    
    return (
      <Link
        to="/print/result"
        state={{
          inspection: inspection,
          turbineInspection: turbineInspection,
          lastTurbineInspections: lastTurbineInspections,
          inspectionModels: inspectionModels,
        }}
        className="ml-3 inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
      >
        <button type="button">
          {/* <!-- Heroicon name: printer --> */}
          <PrinterIcon className="h-6 w-6" />
        </button>
      </Link>
    );
  };

  /**
   * useEffect
   */
  useEffect(() => {
    if (!activeTab) {
      handleChange(DEFAULT_ACTIVE_TAB);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * handleChange
   * @param {*} tab
   */
  const handleChange = (tab) => {
    // setActiveTab(tab);
    navigate(`/report/${tab}`, {
      state: {
        inspection: inspection,
        turbineInspection: turbineInspection,
        lastTurbineInspections: lastTurbineInspections,
      },
    });
  };

  /* useEffect */
  useEffect(() => {
    fetchInspectionModels();

    return () => {
      setInspectionModels([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * fetchInspectionModelItems
   */
  async function fetchInspectionModels() {
    queryInspectionModels(null, setInspectionModels);
  }

  /**
   * isOptionSelected
   * @param {*} selected
   */
  function isOptionSelected(selected) {
    if (parseInt(activeTab) === selected) {
      return "border-teal-500 text-teal-600";
    } else {
      return "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300";
    }
  }

  return (
    <Layout
      title={turbineInspection?.name}
      breadcrumbs={["登録済点検一覧", "登録済点検一覧（号機）", "結果"]}
      printButton={
        <PrintButton
          inspection={inspection}
          turbineInspection={turbineInspection}
          lastTurbineInspections={lastTurbineInspections}
          inspectionModels={inspectionModels}
        />
      }
      inspection={inspection}
      turbineInspection={turbineInspection}
    >
      {!turbineInspection ? (
        <LoadingCard />
      ) : (
        <>
          <InspectionInfo turbineInspection={turbineInspection} />

          <div className="pt-5">
            <div className="sm:hidden">
              <label htmlFor="tabs" className="sr-only">
                選択してください
              </label>
              <select
                id="tabs"
                name="tabs"
                className="block w-full focus:ring-teal-500 focus:border-teal-500 border-gray-300 rounded-md"
                value={activeTab}
                onChange={handleChange}
              >
                {inspectionModels &&
                  inspectionModels.map((model, i) => (
                    <option key={i} value={model.modelNumber}>
                      {model.name}
                    </option>
                  ))}
              </select>
            </div>
            <div className="hidden sm:block">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex" aria-label="Tabs">
                  {inspectionModels &&
                    inspectionModels.map((model, i) => (
                      <button
                        key={i}
                        className={`${isOptionSelected(
                          model.modelNumber
                        )} w-1/4 py-4 px-1 text-center border-b-2 font-medium text-sm`}
                        onClick={() => handleChange(model.modelNumber)}
                      >
                        {model.name}
                      </button>
                    ))}
                </nav>
              </div>
            </div>
          </div>
          {inspectionModels &&
            inspectionModels.map(
              (model, i) =>
                parseInt(activeTab) === model.modelNumber && (
                  <Result
                    key={i}
                    inspection={inspection}
                    inspectionModel={model}
                    turbineInspection={turbineInspection}
                    lastTurbineInspections={lastTurbineInspections}
                  />
                )
            )}
        </>
      )}
    </Layout>
  );
};

export default Report;
